(() => {
  const initResearchReader = (reader) => {
    if (reader.dataset.haroReaderReady === 'true') return;
    const story = reader.closest('.haro-research-story');
    const tablist = story ? story.querySelector('[data-haro-research-tabs]') : null;
    const navLinks = tablist ? [...tablist.querySelectorAll('a[data-haro-topic-id], a[href^="#"]')] : [];
    const sections = navLinks
      .map((link) => document.getElementById(link.dataset.haroTopicId || link.getAttribute('href').slice(1)))
      .filter(Boolean);
    if (!story || !tablist) return;
    if (story.dataset.haroActiveTopic) {
      reader.dataset.haroReaderReady = 'true';
      story.classList.add('is-research-tabs-ready');
      story.querySelectorAll('[data-haro-overview-intro]').forEach((intro) => { intro.hidden = true; });
      navLinks.forEach((link) => {
        const active = link.dataset.haroTopicId === story.dataset.haroActiveTopic;
        link.classList.toggle('is-active', active);
        if (active) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
      });
      sections.forEach((panel) => {
        panel.hidden = false;
        panel.classList.add('is-active');
        panel.querySelectorAll('[data-aos]').forEach((element) => element.classList.add('aos-animate'));
      });
      return;
    }
    if (navLinks.length < 2 || navLinks.length !== sections.length) return;

    reader.dataset.haroReaderReady = 'true';
    story.classList.add('is-research-tabs-ready');
    tablist.setAttribute('role', 'tablist');
    const tablistLabel = tablist.querySelector('.haro-research-menu-label');
    if (tablistLabel) tablistLabel.setAttribute('role', 'presentation');

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const indexById = new Map(sections.map((section, index) => [section.id, index]));
    let activeIndex = 0;

    navLinks.forEach((link, index) => {
      const tabId = `HaroResearchTab${index + 1}`;
      link.id = tabId;
      link.setAttribute('role', 'tab');
      link.setAttribute('aria-controls', sections[index].id);
      sections[index].setAttribute('role', 'tabpanel');
      sections[index].setAttribute('aria-labelledby', tabId);
      const heading = sections[index].querySelector('h2');
      if (heading) heading.tabIndex = -1;
    });

    const resizeActiveMedia = () => {
      requestAnimationFrame(() => {
        const activeSection = sections[activeIndex];
        activeSection.querySelectorAll('[data-aos]').forEach((element) => element.classList.add('aos-animate'));
        activeSection.querySelectorAll('compare-images').forEach((comparison) => {
          comparison.classList.add('aos-animate');
          if (typeof comparison.setOverlapImageSize === 'function') comparison.setOverlapImageSize();
        });
        document.dispatchEvent(new Event('theme:resize'));
      });
    };

    const centerActiveTab = () => {
      const activeTab = navLinks[activeIndex];
      const targetLeft = activeTab.offsetLeft - (tablist.clientWidth - activeTab.offsetWidth) / 2;
      tablist.scrollTo({
        left: Math.max(0, targetLeft),
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    };

    const activate = (index, options = {}) => {
      const {
        focusPanel = false,
        focusTab = false,
        updateHistory = false,
        scrollPanel = false,
        scrollTopics = false,
        showOverviewIntro = false,
      } = options;
      activeIndex = (index + sections.length) % sections.length;

      story.querySelectorAll('[data-haro-overview-intro]').forEach((intro) => {
        intro.hidden = !showOverviewIntro;
      });

      navLinks.forEach((link, linkIndex) => {
        const active = linkIndex === activeIndex;
        link.classList.toggle('is-active', active);
        link.setAttribute('aria-selected', String(active));
        if (active) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
        link.tabIndex = active ? 0 : -1;
      });

      sections.forEach((section, sectionIndex) => {
        const active = sectionIndex === activeIndex;
        section.hidden = !active;
        section.classList.toggle('is-active', active);
      });

      const activeSection = sections[activeIndex];
      if (updateHistory && window.history?.pushState) {
        window.history.pushState(null, '', `#${activeSection.id}`);
      }
      resizeActiveMedia();
      centerActiveTab();

      if (scrollPanel) {
        activeSection.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      }
      if (scrollTopics) {
        const topicMenu = tablist.closest('.haro-research-menu') || tablist;
        topicMenu.scrollIntoView({
          behavior: reducedMotion.matches ? 'auto' : 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      }
      if (focusTab) navLinks[activeIndex].focus({ preventScroll: true });
      if (focusPanel) {
        const heading = activeSection.querySelector('h2');
        if (heading) requestAnimationFrame(() => heading.focus({ preventScroll: true }));
      }
    };

    navLinks.forEach((link, index) => {
      link.addEventListener('click', (event) => {
        if (!link.getAttribute('href').startsWith('#')) return;
        event.preventDefault();
        activate(index, { updateHistory: true, scrollPanel: true });
      });
      link.addEventListener('keydown', (event) => {
        let nextIndex;
        if (event.key === 'ArrowRight') nextIndex = index + 1;
        else if (event.key === 'ArrowLeft') nextIndex = index - 1;
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = sections.length - 1;
        else return;

        event.preventDefault();
        const nextLink = navLinks[(nextIndex + navLinks.length) % navLinks.length];
        if (!nextLink.getAttribute('href').startsWith('#')) window.location.assign(nextLink.href);
        else activate(nextIndex, { focusTab: true, updateHistory: true, scrollPanel: true });
      });
    });

    reader.querySelectorAll('.haro-research-panel-nav a[href^="#"]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const targetId = link.getAttribute('href').slice(1);
        const targetIndex = indexById.get(targetId);
        if (targetIndex === undefined) return;
        event.preventDefault();
        activate(targetIndex, { focusPanel: true, updateHistory: true, scrollPanel: true });
      });
    });

    const activateFromLocation = () => {
      if (story.hasAttribute('data-haro-topic-routes')) {
        const target = [...tablist.querySelectorAll('[data-haro-topic-id]')].find((link) => link.dataset.haroTopicId === window.location.hash.slice(1));
        if (target && !target.getAttribute('href').startsWith('#')) {
          window.location.replace(target.href);
          return;
        }
      }
      const targetId = decodeURIComponent(window.location.hash.slice(1));
      const directIndex = indexById.get(targetId);
      const nestedTarget = targetId ? document.getElementById(targetId) : null;
      const nestedIndex = nestedTarget ? sections.findIndex((section) => section.contains(nestedTarget)) : -1;
      const hasResearchTarget = directIndex !== undefined || nestedIndex >= 0;
      activate(directIndex === undefined ? Math.max(nestedIndex, 0) : directIndex, { scrollPanel: hasResearchTarget, showOverviewIntro: !hasResearchTarget });
    };
    window.addEventListener('popstate', activateFromLocation);
    window.addEventListener('hashchange', activateFromLocation);
    activateFromLocation();
  };

  const init = () => document.querySelectorAll('[data-haro-research-reader]').forEach(initResearchReader);
  const initPillSpill = () => {
    const cards = [...document.querySelectorAll('[data-haro-pill-spill]:not([data-haro-pill-spill-ready])')];
    if (!cards.length) return;
    cards.forEach((card) => { card.dataset.haroPillSpillReady = 'true'; });
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || window.matchMedia('(max-width: 749px)').matches) return;
    if (!('IntersectionObserver' in window)) {
      cards.forEach((card) => card.classList.add('is-spilled'));
      return;
    }
    let observer;
    try {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-spilled');
          observer.unobserve(entry.target);
        });
      }, { threshold: .38 });
    } catch (error) {
      cards.forEach((card) => card.classList.add('is-spilled'));
      return;
    }
    cards.forEach((card) => observer.observe(card));
  };
  const pillNudgeTimers = new WeakMap();
  const initPillScrollNudge = () => {
    const root = document.documentElement;
    if (root.dataset.haroPillScrollNudgeReady === 'true') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || window.matchMedia('(max-width: 749px)').matches) return;
    root.dataset.haroPillScrollNudgeReady = 'true';

    let lastScrollY = window.scrollY;
    let upwardDistance = 0;
    let scheduled = false;
    let lastNudgeAt = 0;

    const updateNudge = () => {
      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollY;
      lastScrollY = currentScrollY;

      if (delta < 0) upwardDistance += Math.abs(delta);
      else if (delta > 0) upwardDistance = 0;

      const now = performance.now();
      if (upwardDistance < 36 || now - lastNudgeAt < 800) return;

      document.querySelectorAll('[data-haro-pill-spill].is-spilled').forEach((card) => {
        const bounds = card.getBoundingClientRect();
        if (bounds.bottom <= 0 || bounds.top >= window.innerHeight) return;

        card.classList.add('is-scroll-up');
        const existingTimer = pillNudgeTimers.get(card);
        if (existingTimer) window.clearTimeout(existingTimer);
        pillNudgeTimers.set(card, window.setTimeout(() => {
          card.classList.remove('is-scroll-up');
          pillNudgeTimers.delete(card);
        }, 420));
      });

      upwardDistance = 0;
      lastNudgeAt = now;
    };

    window.addEventListener('scroll', () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        updateNudge();
        scheduled = false;
      });
    }, { passive: true });
  };
  const initAll = () => {
    init();
    initPillSpill();
    initPillScrollNudge();
  };
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && document.activeElement?.classList.contains('haro-study-term__trigger')) {
      document.activeElement.blur();
    }
  });
  document.addEventListener('shopify:section:load', initAll);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll, { once: true });
  else initAll();
})();
